import { useMemo } from 'react';

import { useMessageInstanceEvent } from '../index';
import { useMessageContext } from '../message-context';
import { postNarrowExposureDrop } from './drop-event';
import { evaluateCohort } from './evaluate-cohort';
import type { NarrowExposureConfig } from './types';

export type { NarrowExposureCohort, NarrowExposureConfig } from './types';

/**
 * Choreographer `shouldClaim` for the current message's narrow exposure experiment.
 *
 * Returns `undefined` when the message has no experiment, so the choreographer claims as usual.
 * Each offer reads the cohort, which logs the exposure. Show cohorts claim the slot. Drop and
 * unevaluated cohorts post a `dropped` event, with cohort `unevaluated` when Statsig returned no
 * usable cohort, and decline the slot so the next queued message is offered.
 */
export const useNarrowExposureShouldClaim = (
	config: NarrowExposureConfig | null | undefined,
): (() => boolean) | undefined => {
	const { messageInstanceId } = useMessageContext();
	const { messageInstanceDropped } = useMessageInstanceEvent();

	return useMemo(() => {
		if (!config) {
			return undefined;
		}

		return (): boolean => {
			const decision = evaluateCohort(config);
			if (decision.outcome === 'show') {
				return true;
			}

			postNarrowExposureDrop(messageInstanceDropped, messageInstanceId, config, decision);
			return false;
		};
	}, [config, messageInstanceId, messageInstanceDropped]);
};
